const PROFILE_HTML = `<main>
  <article class="profile-card">
    <h1>Alex Morgan</h1>
    <img src="https://placehold.co/160x160" alt="Portrait placeholder for Alex Morgan">
    <p>Computer science student interested in building useful interfaces.</p>
    <ul>
      <li>Interface design</li>
      <li>Web development</li>
      <li>Photography</li>
    </ul>
    <a href="https://www.vt.edu/">Visit Virginia Tech</a>
  </article>
</main>`

const HTML_ASSIGNMENT = `Build a student profile card

Create the HTML structure for a student profile card. Do not add CSS or JavaScript yet.

Your page must include:

1. A main element containing one article.
2. An h1 with the student's name.
3. An image with meaningful alternative text.
4. A paragraph introducing the student.
5. A list with at least three interests.
6. A link to a portfolio or university webpage.

Select Render HTML when you want to inspect your page. Use Run Tests to check its structure.`

const CSS_ASSIGNMENT = `Style a student profile card

The HTML is provided. Write CSS that turns it into a readable profile card.

Your stylesheet must:

1. Style the profile-card class.
2. Set a maximum width for the card.
3. Add padding and a visible border.
4. Round the profile image.
5. Style the link with a color.

Select Render CSS to inspect the page. Use Run Tests to check the stylesheet.`

const JAVASCRIPT_ASSIGNMENT = `Add behavior to a profile card

The HTML is provided. Write JavaScript so the Show interests button toggles the interests list.

Your script must:

1. Select the button and interests list.
2. register a click event listener.
3. Toggle the list's hidden state when clicked.
4. Update the button text to describe the next action.

Select Render JavaScript to load your current script, then interact with the button.`

const THREE_FILE_ASSIGNMENT = `Build an interactive profile card

Use the HTML, CSS, and JavaScript files together. Select Render Project whenever you want to load the current three-file project in the preview.`

const HTML_TESTS = [
  'Main contains a profile article',
  'Non-empty page heading',
  'Accessible profile image',
  'Introduction paragraph',
  'At least three interests',
  'Link with a destination',
]

// Initial/Final Comparison: the student only ever sees/edits HTML (enabledFiles below).
// There is deliberately no meaningful shared stylesheet here - the CSS pane is normally
// left empty, and the Target render's styling is written directly inline (style="...")
// on its own HTML. Earlier this type used a shared, hidden external stylesheet keyed by
// class/id names (e.g. a hidden `#specialitem` rule the target HTML had to reference) -
// that was scrapped because it let the AI assistant "win" by pattern-matching the
// student's request against literal class/id strings visible in the CSS text it was
// given, regardless of how well the student actually described the visual delta. Inline
// styles on the target HTML remove that shortcut entirely: there is no hidden vocabulary
// to guess or leak, so success genuinely depends on the quality of the student's prompt.
// This default pair is just the out-of-the-box example - each real session's actual
// initial/target content is authored per-session via GuiExerciseEditor
// (session.test_code.template / .finalTemplate), same as starterCode is for every other type.
const INITIAL_FINAL_ASSIGNMENT = `Use the AI assistant to generate HTML code that transforms the webpage from the initial appearance into the target appearance. Complete the task using as few prompts as possible, with a minimum of one prompt. You may not edit the HTML code manually. Your only permitted manual action is pasting the AI-generated code into the HTML editor.

Note: The AI assistant can access your current HTML code but cannot see the Initial, Target, or Live Preview panels.`

const INITIAL_FINAL_SHARED_CSS = ''

// Full documents, not body-only fragments: these students have only ever seen HTML with
// the <!doctype>/<html>/<head>/<body> boilerplate around it, so a body-only starter would
// look like a different, unfamiliar language. Renders fine inside LivePreview's own
// <body> substitution regardless (standard HTML5 parsing drops the nested
// doctype/html/head/body tags and keeps their contents - verified separately).
const INITIAL_FINAL_STARTER_HTML = `<!doctype html>
<html>
<head>
  <title>Skills</title>
</head>
<body>
  <h2>Skills</h2>
  <ul>
    <li>HTML</li>
    <li>CSS</li>
    <li>JavaScript</li>
  </ul>
</body>
</html>`

const INITIAL_FINAL_TARGET_HTML = `<!doctype html>
<html>
<head>
  <title>Skills</title>
</head>
<body>
  <h2 style="font-size: 24px; color: #1c6ea4;">Skills</h2>
  <ul>
    <li style="color: #c0392b; font-weight: bold;">HTML</li>
    <li>CSS</li>
    <li>JavaScript</li>
  </ul>
</body>
</html>`

// Initial/Final Comparison — CSS variant: same "describe the visual delta, let the AI
// write the code" mechanic as InitialFinal above, retargeted at responsive CSS (flexible
// layouts/media, not HTML class/id structure) per the professor's follow-up request. Two
// deliberate differences from the HTML version, both instructor requirements:
// 1. Only ONE live view (this type's "Initial" reference IS the student's own live
//    preview - there's no separate frozen "Initial" pane like InitialFinal has, since the
//    starter CSS is non-responsive and the student's own edits are what's being tested for
//    responsiveness as they resize).
// 2. The Target is a plain instructor-uploaded IMAGE (finalImage, below), not a
//    live-rendered page. A live-rendered Target would let a student open DevTools and read
//    the exact CSS values needed - fine for the HTML version (the "answer" there is just a
//    class/id name a hidden stylesheet already exposes structurally), but fatal here, where
//    the answer IS specific CSS values. A flat image can be looked at but not inspected.
const INITIAL_FINAL_CSS_ASSIGNMENT = `Use the AI assistant to generate CSS that makes this image responsive so it matches the Target, then drag the divider between the editor and Your View to confirm it holds up across widths. Complete the task using as few prompts as possible, with a minimum of one prompt. You may not edit the CSS code manually. Your only permitted manual action is pasting the AI-generated code into the CSS editor.

Note: The AI assistant can see your current CSS and a screenshot of your current rendered view, but cannot see the Target.`

// A single fixed-width image, deliberately non-responsive - the lecture's own
// img { width: 100% } DIY. The out-of-the-box default below is just a placeholder
// (placehold.co) - a real session's actual starter image is authored per-session via
// GuiExerciseEditor's Starter Image upload (it rewrites the <img> src in starterCode.html
// directly - see compressImageFile/setImageSrcInHtml in GuiExerciseEditor.jsx), same as
// finalImage is for the Target. Deliberately no card/heading/paragraph content around it -
// this type is specifically about resizing images ("resizing their window to see if they
// get it right" per the professor), not HTML structure, and a bare image is also the only
// shape that reliably fits the achievable width of the IDE/"Your View" split at any drag
// position (a multi-item flex layout was tried first and could go wider than that split
// ever reaches).
const INITIAL_FINAL_CSS_STARTER_HTML = `<!doctype html>
<html>
<head>
  <title>Responsive Image</title>
</head>
<body>
  <img class="hero" src="https://placehold.co/480x270" alt="Responsive image exercise">
</body>
</html>`

const INITIAL_FINAL_CSS_STARTER_CSS = `.hero {
  width: 480px;
}`

// Initial/Final Comparison — JavaScript variant: same "describe the delta, let the AI
// write the code" mechanic as InitialFinal/InitialFinalCSS, retargeted at JS behavior
// (event listeners, query selectors) instead of visual appearance. Two deliberate
// differences from both existing variants:
// 1. All three files (html/css/js) are student-editable (enabledFiles below), not just
//    one - the exercise is specifically about JS, but the scaffold it runs against still
//    needs to be adjustable. Uses the default TABBED editor layout, not JSBin's 'columns'
//    mode: columns mode's per-pane telemetry (CodeMirrorPane in CodeEditor.jsx) only ever
//    logs that one pane's raw text, not the combined {html,css,js} JSON the instructor
//    replay viewer (workspaceReplay2/useReplayState.js) needs to reconstruct the session -
//    tabbed mode's shared-EditorView telemetry already logs the full JSON on every
//    keystroke, which is what makes replay actually work.
// 2. The Target is a LIVE, interactive render of the real target JavaScript (like
//    InitialFinal's HTML Target, not InitialFinalCSS's flat image Target). This was a
//    deliberate, explicit choice made with the instructor knowing the tradeoff: unlike CSS
//    values (impossible to reverse-engineer from a flat image), a live-rendered JS
//    Target's actual script is readable via the browser's DevTools by a student who goes
//    looking - there is no way to demonstrate interactive behavior live without also
//    exposing its source this way. Accepted anyway because a live Target is far more
//    useful for a JS exercise (students can click through the accordion behavior
//    themselves) than a static recording would be.
const INITIAL_FINAL_JS_ASSIGNMENT = `Use the AI assistant to generate JavaScript that changes this FAQ list's behavior from the Initial version to the Target version. Complete the task using as few prompts as possible, with a minimum of one prompt. You may edit the HTML and CSS directly if you want to, but the JavaScript should only ever be pasted in from the AI assistant's replies - do not hand-write or hand-edit it yourself.

Note: The AI assistant can access your current HTML, CSS, and JavaScript, but cannot see or interact with the Initial or Target panels - only what you tell it about the difference in behavior.`

const INITIAL_FINAL_JS_STARTER_HTML = `<!doctype html>
<html>
<head>
  <title>FAQ</title>
</head>
<body>
  <div class="faq">
    <div class="faq-item">
      <button class="faq-question" type="button">What is an event listener?</button>
      <p class="faq-answer" hidden>A function that runs in response to a specific action, like a click.</p>
    </div>
    <div class="faq-item">
      <button class="faq-question" type="button">What does querySelector do?</button>
      <p class="faq-answer" hidden>It finds the first element in the page matching a CSS-style selector.</p>
    </div>
    <div class="faq-item">
      <button class="faq-question" type="button">What does querySelectorAll do?</button>
      <p class="faq-answer" hidden>It finds every matching element on the page and returns them as a list.</p>
    </div>
  </div>
</body>
</html>`

const INITIAL_FINAL_JS_STARTER_CSS = `.faq {
  max-width: 28rem;
  margin: 0 auto;
  font-family: sans-serif;
}
.faq-item {
  border-bottom: 1px solid #d1d5db;
}
.faq-question {
  width: 100%;
  text-align: left;
  padding: 12px 8px;
  background: none;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}
.faq-answer {
  margin: 0 0 12px;
  padding: 0 8px;
  color: #374151;
}`

// Starter behavior is deliberately incomplete, not broken: each question already toggles
// its own answer independently - the gap the student has to describe is that opening one
// answer should close any other answer that's currently open (real accordion semantics).
const INITIAL_FINAL_JS_STARTER_JS = `document.querySelectorAll('.faq-question').forEach(function (button) {
  button.addEventListener('click', function () {
    var answer = button.nextElementSibling
    answer.hidden = !answer.hidden
  })
})`

const INITIAL_FINAL_JS_TARGET_JS = `document.querySelectorAll('.faq-question').forEach(function (button) {
  button.addEventListener('click', function () {
    var answer = button.nextElementSibling
    var isHidden = answer.hidden
    document.querySelectorAll('.faq-answer').forEach(function (a) {
      a.hidden = true
    })
    answer.hidden = !isHidden
  })
})`

const TYPE_CONFIGS = {
  HTML: {
    phase: 'html',
    enabledFiles: ['html'],
    previewLabel: 'Render HTML',
    assignment: HTML_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: '<main>\n  <!-- Build your profile card here. -->\n</main>',
      css: '',
      js: '',
    }),
    tests: HTML_TESTS,
  },
  CSS: {
    phase: 'css',
    enabledFiles: ['css'],
    previewLabel: 'Render CSS',
    assignment: CSS_ASSIGNMENT,
    starterCode: JSON.stringify({ html: PROFILE_HTML, css: '', js: '' }),
    tests: [
      'Profile card selector',
      'Maximum card width',
      'Card padding',
      'Visible card border',
      'Rounded profile image',
      'Colored link',
    ],
  },
  JavaScript: {
    phase: 'javascript',
    enabledFiles: ['js'],
    previewLabel: 'Render JavaScript',
    assignment: JAVASCRIPT_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: `${PROFILE_HTML.replace('<ul>', '<button id="toggle-interests" type="button">Hide interests</button>\n    <ul id="interests">')}`,
      css: '.profile-card { max-width: 32rem; padding: 1rem; border: 1px solid #cccccc; }',
      js: '// Add the button behavior here.\n',
    }),
    tests: [
      'Selects the toggle button',
      'Selects the interests list',
      'Registers a click listener',
      'Changes the interests visibility',
    ],
  },
  // Integrated HTML+CSS+JS phase. A first-class, instructor-creatable type
  // (not limited to the legacy session-668 pin below). The workspace only
  // runs a syntax check for this phase (see TerminalContext's fallback
  // branch), so it deliberately declares a single test label to match the
  // single pass/fail result the terminal produces.
  Mixed: {
    phase: 'integrated',
    enabledFiles: ['html', 'css', 'js'],
    previewLabel: 'Render Project',
    assignment: THREE_FILE_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: '<h1>Hello World</h1>\n<p>Welcome to Web Development!</p>\n<button id="btn">Click Me</button>',
      css: 'body {\n  font-family: sans-serif;\n  text-align: center;\n  padding-top: 50px;\n}\nbutton {\n  padding: 10px 20px;\n  font-size: 16px;\n}',
      js: "document.getElementById('btn').addEventListener('click', () => {\n  alert('Button clicked!');\n});",
    }),
    tests: ['HTML, CSS, and JavaScript syntax checks'],
  },
  // JS Bin-style layout: HTML, CSS, and JS all visible and editable at once in their own
  // resizable panes (see CodeEditor.jsx's 'columns' mode), instead of Mixed's single pane
  // switched by tabs. Structurally identical to Mixed otherwise (same three files, same
  // starter/assignment/checks) - a first pass to validate the layout direction before
  // deciding whether to retarget existing Mixed sessions at it.
  JSBin: {
    phase: 'integrated',
    enabledFiles: ['html', 'css', 'js'],
    previewLabel: 'Run with JS',
    previewTabLabel: 'Output',
    layout: 'columns',
    assignment: THREE_FILE_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: '<h1>Hello World</h1>\n<p>Welcome to Web Development!</p>\n<button id="btn">Click Me</button>',
      css: 'body {\n  font-family: sans-serif;\n  text-align: center;\n  padding-top: 50px;\n}\nbutton {\n  padding: 10px 20px;\n  font-size: 16px;\n}',
      js: "document.getElementById('btn').addEventListener('click', () => {\n  alert('Button clicked!');\n});",
    }),
    tests: ['HTML, CSS, and JavaScript syntax checks'],
  },
  // Initial/Final Comparison: student sees two fixed reference renders (Initial, Target)
  // built from a shared/hidden CSS, edits ONLY html (see CodeEditor's hideDisabledTabs,
  // wired in Workspace for layout:'compare'), and uses the AI assistant - scoped to a
  // dedicated, non-Socratic system prompt (getInitialFinalSystemPrompt in systemPrompt.js)
  // that is never shown the target HTML - to try to close the visual gap. Nothing is
  // graded (tests: []); this measures prompt quality, not code correctness.
  InitialFinal: {
    phase: 'html-target',
    enabledFiles: ['html'],
    layout: 'compare',
    previewLabel: 'Render',
    assignment: INITIAL_FINAL_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: INITIAL_FINAL_STARTER_HTML,
      css: INITIAL_FINAL_SHARED_CSS,
      js: '',
    }),
    finalHtml: INITIAL_FINAL_TARGET_HTML,
    tests: [],
  },
  // Initial/Final Comparison — CSS variant (see comment above the constants for the two
  // deliberate differences from InitialFinal: single live view, image-based Target).
  // aiVisualContextEnabled overrides COMMON_CONFIG's default off - the AI needs to see the
  // student's rendered layout to translate a described visual delta into CSS, since CSS
  // effects are harder to fully verbalize than the HTML content changes InitialFinal deals
  // with. It never receives finalImage - see getInitialFinalCSSSystemPrompt in
  // systemPrompt.js.
  InitialFinalCSS: {
    phase: 'css-target',
    enabledFiles: ['css'],
    layout: 'compare-image',
    previewLabel: 'Render CSS',
    assignment: INITIAL_FINAL_CSS_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: INITIAL_FINAL_CSS_STARTER_HTML,
      css: INITIAL_FINAL_CSS_STARTER_CSS,
      js: '',
    }),
    // No default target image - each real session's target is authored per-session via
    // GuiExerciseEditor (session.test_code.finalTemplate.image), same as InitialFinal's
    // finalHtml. An empty value here just means the Target pane shows a placeholder
    // until an instructor uploads one.
    finalImage: '',
    aiVisualContextEnabled: true,
    tests: [],
  },
  // Initial/Final Comparison — JavaScript variant (see comment above the constants for the
  // two deliberate differences from the HTML/CSS variants: all three files editable via
  // the tabbed layout, and a live rather than static Target).
  InitialFinalJS: {
    phase: 'js-target',
    enabledFiles: ['html', 'css', 'js'],
    layout: 'compare-js',
    previewLabel: 'Render',
    assignment: INITIAL_FINAL_JS_ASSIGNMENT,
    starterCode: JSON.stringify({
      html: INITIAL_FINAL_JS_STARTER_HTML,
      css: INITIAL_FINAL_JS_STARTER_CSS,
      js: INITIAL_FINAL_JS_STARTER_JS,
    }),
    finalJs: INITIAL_FINAL_JS_TARGET_JS,
    tests: [],
  },
}

// Expression form of the structural checks TerminalContext.js has
// hardcoded per phase (see runTestsWebDev). Each `check` is a JS expression
// string evaluated with `htmlDoc` (a parsed Document), `css`, and `js` (raw
// source strings) in scope - same shape an instructor's custom tests take
// once saved to session.test_code.unit_test (see getGuiSessionConfig below).
// These defaults are NEVER read by TerminalContext directly; they only seed
// GuiExerciseEditor's test-editing buffer so instructors start from
// something that mirrors current pass/fail behavior instead of a blank
// slate. Existing sessions with no saved custom tests keep running the
// hardcoded phase branches untouched.
const DEFAULT_CHECKS = {
  HTML: [
    { name: 'Main contains a profile article', check: "Boolean(htmlDoc.querySelector('main')?.querySelector('article'))" },
    { name: 'Non-empty page heading', check: "Boolean(htmlDoc.querySelector('h1')?.textContent.trim())" },
    { name: 'Accessible profile image', check: "Boolean(htmlDoc.querySelector('img')?.getAttribute('alt')?.trim())" },
    { name: 'Introduction paragraph', check: "Boolean(htmlDoc.querySelector('p')?.textContent.trim())" },
    { name: 'At least three interests', check: "Boolean(htmlDoc.querySelector('ul, ol')) && htmlDoc.querySelectorAll('ul li, ol li').length >= 3" },
    { name: 'Link with a destination', check: "Boolean(htmlDoc.querySelector('a[href]')?.getAttribute('href')?.trim())" },
  ],
  CSS: [
    { name: 'Profile card selector', check: "/\\.profile-card\\s*\\{/i.test(css)" },
    { name: 'Maximum card width', check: "/max-width\\s*:/i.test((css.match(/\\.profile-card\\s*\\{([^}]*)\\}/i)||[])[1]||'')" },
    { name: 'Card padding', check: "/padding(?:-[a-z]+)?\\s*:/i.test((css.match(/\\.profile-card\\s*\\{([^}]*)\\}/i)||[])[1]||'')" },
    { name: 'Visible card border', check: "/border(?:-[a-z]+)?\\s*:/i.test((css.match(/\\.profile-card\\s*\\{([^}]*)\\}/i)||[])[1]||'')" },
    { name: 'Rounded profile image', check: "/border-radius\\s*:/i.test((css.match(/(?:img|\\.profile-card\\s+img)\\s*\\{([^}]*)\\}/i)||[])[1]||'')" },
    { name: 'Colored link', check: "/color\\s*:/i.test((css.match(/(?:a|\\.profile-card\\s+a)\\s*\\{([^}]*)\\}/i)||[])[1]||'')" },
  ],
  JavaScript: [
    { name: 'Selects the toggle button', check: "/(?:getElementById|querySelector)\\s*\\([^)]*toggle-interests/i.test(js)" },
    { name: 'Selects the interests list', check: "/(?:getElementById|querySelector)\\s*\\([^)]*interests/i.test(js)" },
    { name: 'Registers a click listener', check: "/addEventListener\\s*\\(\\s*['\\\"]click['\\\"]/i.test(js)" },
    { name: 'Changes the interests visibility', check: "/(?:hidden|display|classList)/i.test(js)" },
  ],
  Mixed: [
    { name: 'HTML, CSS, and JavaScript syntax checks', check: 'true' },
  ],
  JSBin: [
    { name: 'HTML, CSS, and JavaScript syntax checks', check: 'true' },
  ],
  InitialFinal: [],
  InitialFinalCSS: [],
  InitialFinalJS: [],
}

const SESSION_ID_CONFIGS = {
  // Preserve the original session 668 demonstration as a three-file project.
  // New integrated sessions should use type: 'Mixed' (above) instead of an
  // id pin.
  668: TYPE_CONFIGS.Mixed,
}

const COMMON_CONFIG = {
  previewMode: 'manual',
  externalResourcesAllowed: false,
  mobilePreviewEnabled: false,
  // Screenshot + DOM snapshot capture of the rendered preview (Visual UI Trace).
  // Consent modal discloses this (see ConsentModal.jsx visualTraceEnabled prop) whenever this is true.
  visualTraceEnabled: true,
  // Whether the captured screenshot is attached to the AI tutor's request (sent to the
  // LLM provider). Separate from visualTraceEnabled so a session can capture snapshots
  // for the research dataset without also sending student screen content to a third party.
  aiVisualContextEnabled: false,
  // Whether /workspace/:id requires a real (Cognito) student sign-in/sign-up instead of
  // the anonymous guest-join button - see Login.jsx. Off by default: turning this on for a
  // real class requires updating the consent text and IRB #26-039 first, since the current
  // consent language promises guest-only anonymous participation.
  requireStudentAccount: false,
  // 'tabs' (default): one code pane, switched between enabledFiles via tabs (CodeEditor.jsx's
  // original behavior). 'columns': all enabledFiles shown and editable simultaneously in
  // side-by-side panes (see CodeEditor.jsx's columns mode) - a JS Bin-style layout, opted
  // into per session type via this flag rather than checking `type === 'JSBin'` in multiple
  // places.
  layout: 'tabs',
  // Label for the right-panel tab that shows LivePreview (Workspace.jsx) - separate from
  // previewLabel (the render button's own text) since this names the *tab*, not the action.
  previewTabLabel: 'Live Preview',
}

export const GUI_SESSION_TYPES = Object.keys(TYPE_CONFIGS)

export function getGuiSessionConfig(sessionOrId) {
  const session = typeof sessionOrId === 'object' ? sessionOrId : null
  const configKey = (session?.type && TYPE_CONFIGS[session.type] && session.type)
    || (SESSION_ID_CONFIGS[Number(session?.id ?? sessionOrId)] ? 'Mixed' : null)
  const config = configKey && TYPE_CONFIGS[configKey]
  if (!config) return null

  const merged = { ...COMMON_CONFIG, ...config }
  merged.type = configKey
  merged.defaultChecks = DEFAULT_CHECKS[configKey] || []

  // An instructor-saved starter project (see GuiExerciseEditor) lives in
  // session.test_code.template as a JSON string of { html, css, js } - the
  // same shape as the hardcoded default above. Prefer it when present so
  // every consumer of getGuiSessionConfig (CodeContext, Workspace, the
  // AI prompt builder) picks up instructor edits without extra wiring.
  const customStarter = session?.test_code?.template
  if (typeof customStarter === 'string' && customStarter.trim()) {
    try {
      JSON.parse(customStarter)
      merged.starterCode = customStarter
    } catch (e) {
      // Malformed saved starter code - fall back to the phase default.
    }
  }

  // An instructor-saved custom test suite (see GuiExerciseEditor) lives in
  // session.test_code.unit_test as a JSON string of [{name, check}, ...].
  // When present, both the displayed test names (merged.tests) and the
  // actual checks TerminalContext.runTestsWebDev executes (merged.checks)
  // come from this single array, so labels and pass/fail results can never
  // drift apart. Sessions with no saved custom tests keep running the
  // hardcoded phase branches in TerminalContext untouched (merged.checks
  // stays undefined).
  const customTests = session?.test_code?.unit_test
  if (typeof customTests === 'string' && customTests.trim()) {
    try {
      const parsedTests = JSON.parse(customTests)
      if (Array.isArray(parsedTests) && parsedTests.every((t) => t && typeof t.name === 'string' && typeof t.check === 'string')) {
        merged.checks = parsedTests
        merged.tests = parsedTests.map((t) => t.name)
      }
    } catch (e) {
      // Malformed saved tests - fall back to the phase default checks.
    }
  }

  // Initial/Final Comparison's target render (see GuiExerciseEditor) lives in
  // session.test_code.finalTemplate as a JSON string of { html } - deliberately no `css`
  // field (see INITIAL_FINAL_SHARED_CSS comment above: the target's styling is written
  // inline in its own HTML, not in a separate stylesheet). The CSS variant instead stores
  // { image } - a data URL for the instructor-uploaded target screenshot/mockup (see
  // InitialFinalCSS comment above for why this one is an image rather than a live render).
  // The JS variant stores { js } - a single script string, live-rendered alongside the
  // *starter* html/css (see InitialFinalJS comment above for why the Target has no html/css
  // of its own).
  const customFinal = session?.test_code?.finalTemplate
  if (typeof customFinal === 'string' && customFinal.trim()) {
    try {
      const parsedFinal = JSON.parse(customFinal)
      if (parsedFinal && typeof parsedFinal.html === 'string') {
        merged.finalHtml = parsedFinal.html
      }
      if (parsedFinal && typeof parsedFinal.image === 'string') {
        merged.finalImage = parsedFinal.image
      }
      if (parsedFinal && typeof parsedFinal.js === 'string') {
        merged.finalJs = parsedFinal.js
      }
    } catch (e) {
      // Malformed saved target HTML/image/js - fall back to the phase default.
    }
  }

  return merged
}

export function isGuiSession(sessionOrId) {
  return Boolean(getGuiSessionConfig(sessionOrId))
}
